import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";

import { RootState } from '../../../../store';
import TextBox from '../../elements/textbox';
import Button from '../../elements/button';
import { currencyFormatter } from '../../../../utils/currency';
import { config } from '../../../../utils/config/';

import { ReactComponent as Close } from '../../../../assets/icons/close.svg';
import { ReactComponent as NoEntry } from '../../../../assets/icons/no-entry.svg';
import { ReactComponent as PromoCoupon } from '../../../../assets/icons/promo-coupon.svg'
import { ReactComponent as DropdownArrow } from '../../../../assets/icons/collapseArrow.svg';
import { CouponState } from '../../../../enums/coupon';
import { CartOperation } from '../../../../enums/cartOperation';
import { setCoupon, setDisabledItem, setShowWarningOnCartPage, startLoading, setSentryNavigationTrackingState, setSentryTransactionState, stopLoading, logError, setIsShippingCouponFree, setValidatedCoupon } from '../../../../store/application';
import { onProceedToCheckoutOperation } from '../../../shopping-bag/ga-module';
import { useValidateCouponMutation } from '../../../../store/discount';
import { cartApi, useSetUserTypeAndAutoApplyMutation } from '../../../../store/cart';
import { get as getCookie, getBrandKeywordCampaignParameters, delete_cookie } from '../../../../utils/cookies';
import paypal from '../../../../store/paypal';
import { Page } from "../../../../enums/page";
import i18n from '../../../../utils/translations/i18n';
import {displayPayPalPayLaterOffer} from "../../../../utils/paypal";
import { GaPageType } from '../../../../enums/gaPageType';
import { ApplePay } from '../../../checkout-payment/braintree/braintree-web/apple-pay/apple-pay';


import './index.scss';
import { newCartApi, useRemoveCouponMutation } from '../../../../store/newCart';
import { isApplePaySupported } from '../../../../enums/checkoutPayments';
import GlobalDiscountSummary from '../global-discount-summary';
import { getTaxShippingAndSaving } from '../order-summary-component/common/price-summary/utils';
import PromoCodes from '../promo-codes';
import CouponSummary from '../couponSummary';
import CustomAccordion from '../../elements/accordian';
import { calculateThresholdStatus } from '../../../../utils/cart';
import { CartInteraction } from '../../../../enums/cartInteraction';
import { cartInteractionEvent } from '../../../../utils/analytics/google';


const OrderSummary = ({ cart, user, error, globalDiscountData }: any) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = i18n;
    const {
        applicationState: { coupon, currentPage, disabledItem,globalDiscountShippingType,validatedCoupon,isShippingDisc, currentOperation, showWarning,globalThresholdMet,isGlobalCartDiscountFree,isShippingCouponFree  }
    }: any = useSelector((state: RootState) => state);
    const [showPaymentError, setShowPaymentError] = useState(false);

    const [validateCoupon] = useValidateCouponMutation();
    const [removeCoupon] = useRemoveCouponMutation();
    const [isFocused, setIsFocused] = useState(false);
    const [setCustomFieldAndAutoApply] = useSetUserTypeAndAutoApplyMutation();
    const [triggerMiniCartQuery] = cartApi.endpoints.getLatestMiniCart.useLazyQuery();
    const totalCartAmount = Number(((cart?.cartTotal / Math.pow(10, cart?.fractionDigits)) || 0)?.toFixed(cart?.fractionDigits));
    const cartSubTotalCent = cart.cartSubtotal;
    const fractionDigit = cart.fractionDigits;
    const cartSubtotal = fractionDigit ? (cartSubTotalCent / Math.pow(10, fractionDigit)).toFixed(fractionDigit) : cartSubTotalCent;
    const [isExpanded, setIsExpanded] = useState(false);
    let specialOfferCookie = getBrandKeywordCampaignParameters(getCookie('np.brandKeywordCampaign')).couponCode;


    const { isThresholdMet } = calculateThresholdStatus(cart, coupon?.thresholdValue);
    const showPayPalPayLaterOffer = displayPayPalPayLaterOffer(config("isPayPalPayLaterEnabled") === "true",Number(config("payPalPayLaterMin")),Number(config("payPalPayLaterMax")),totalCartAmount);

    // Checks if a discount exists, either directly applied to the cart or through a shipping discount that meets threshold or is a shipping discount without a threshold when shipping customLineItem is not created.
    const doesDiscountExist = (cart?.discountExists || (!cart?.shippingCustomItem && coupon?.thresholdValue && isThresholdMet && isShippingDisc) || (!cart?.shippingCustomItem && !coupon?.thresholdValue && isShippingDisc)) ?? false;
    const [couponText, setCouponText] = useState('');

    const togglePromoAccordion = () => {
        if(!isExpanded) cartInteractionEvent(CartInteraction.PROMO_CODE_EXPAND)
        setIsExpanded(!isExpanded);
    };

    const handleCouponChange = (event, coupon, error, currentOperation, dispatch) => {
        const text = event.target.value.toUpperCase();
        setCouponText(text);
    
        if (!text) {
            if (validatedCoupon?.state === CouponState.INVALID || validatedCoupon?.state === CouponState.EXPIRED) {
                const newState = !specialOfferCookie ? CouponState.INITIAL : CouponState.CLEARED;
                dispatch(setValidatedCoupon({ state: newState }));
            }
            if (error?.isError && (error?.errorObj.operation === CartOperation.VALIDATECOUPON || currentOperation?.name === CartOperation.VALIDATECOUPON)) {
                dispatch(logError(false, null, null));
            }
        }
    };


    //when there is no coupon in brandkeyword we will set valid coupon to false and hence input box should be empty
    useEffect(() => {
        if(validatedCoupon?.state === CouponState.INITIAL){
            setCouponText('');
        }
    },[validatedCoupon]);

    //if coupon is not giving discount we still show banner at that time input box should be empty
    useEffect(() => {
        if (coupon?.state === CouponState.INELIGIBLE || coupon?.state === CouponState.THRESHOLD_NOT_MET) {
            setCouponText('');
            setIsExpanded(true);
        }
    },[coupon])

    useEffect(() => {
        if (doesDiscountExist && (validatedCoupon?.state === CouponState.BACKEND_ERROR) && cart?.validDiscountCode) {
            setCouponText('');
            dispatch(setIsShippingCouponFree(false))
            delete_cookie('np.brandKeywordCampaign');
            dispatch(startLoading());
            removeCoupon({ cartId: cart.id, discountCode: cart.validDiscountCode || cart.customerDiscountCoupon });
        }
    }, [doesDiscountExist,validatedCoupon])


   useEffect(() => {
       if ((config("isPayPalPayLaterEnabled") === "true" || (config('isNASite') && config('isPaypalEnabled') === 'true')) && cart?.id && cart?.cartSubtotal) {
            const shippingType = cart.shippingType ?? '';
            dispatch(paypal.loadPaypal(cart.id, user, shippingType, setShowPaymentError, cart, navigate, triggerMiniCartQuery));
        }

        let couponToBeApplied = cart.validDiscountCode;
        if (specialOfferCookie) {
            if (user.isLoggedIn === 'true') {
                if (cart.validDiscountCode === '') {
                    couponToBeApplied = specialOfferCookie;
                }
            } else {
                couponToBeApplied = specialOfferCookie;
            }
        }
        const isFirstTimeBuyerCart = getCookie('UserType') === 'rtb' ? false : true;
        setCustomFieldAndAutoApply({isFirstTimeBuyerCart: isFirstTimeBuyerCart , discountCode: couponToBeApplied});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); 
    
    const applyCouponHandler = () => {
        if (couponText && couponText !== specialOfferCookie) {  
          dispatch(setSentryTransactionState("applyCoupon","Applying Promo Code on cart Page"));
          dispatch(startLoading());
          dispatch(setIsShippingCouponFree(false))
          validateCoupon({ couponCode: couponText, 'locale': config('locale'), callApplyCoupon: true, isCouponManuallyApplied : true });
        }
    }
    const removeCouponHandler = () => {
        if(cart?.validDiscountCode || validatedCoupon?.state !== CouponState.INITIAL){
            setCouponText('');
            setIsExpanded(true);
            dispatch(setIsShippingCouponFree(false))
            delete_cookie('np.brandKeywordCampaign');
            cartInteractionEvent(CartInteraction.CHANGE_PROMO_CODE)
            if(cart?.validDiscountCode){
                dispatch(startLoading());
                removeCoupon({ cartId: cart.id, discountCode: cart.validDiscountCode || cart.customerDiscountCoupon });
            }else{
                dispatch(setValidatedCoupon({ state: CouponState.INITIAL}))
            }
        }
    }

    const promoCouponApplyHandler = (couponCode) => {
        if(couponCode){
          dispatch(startLoading());
          setIsFocused(true);
          setCouponText(couponCode);
          dispatch(setIsShippingCouponFree(false))
          validateCoupon({ couponCode: couponCode, 'locale': config('locale'), callApplyCoupon: true });
        }
    }

    const onCheckoutButtonClick = async () => {
        dispatch(setSentryNavigationTrackingState(Page.SHOPPINGBAG, Page.CHECKOUTSHIPPING));
        if(disabledItem) {
            dispatch(setDisabledItem(false));
        }
        if(showWarning){
            dispatch(setShowWarningOnCartPage(false));
        }
        onProceedToCheckoutOperation(cart, currentPage,coupon,isShippingDisc);
        navigate(`/${config('locale_details').current_sitename}/${t('checkout_shipping.url')}`);

    }
    let isCouponError = 
        validatedCoupon?.state === CouponState.INVALID ||
        validatedCoupon?.state === CouponState.BACKEND_ERROR ||
        validatedCoupon?.state === CouponState.EXPIRED ||
        (error?.isError && (error?.errorObj.operation === CartOperation.VALIDATECOUPON && currentOperation?.name === CartOperation.VALIDATECOUPON) && couponText);
    const proceedToCheckout = t('shopping_cart_details.proceed_to_checkout');
    const toCapitalized = config('withCapitalized') && config('locale') ? config('withCapitalized').includes(config('locale')):false;

    const PromoCodeManager = ({
        couponText, 
        handleCouponChange, 
        coupon, 
        error, 
        currentOperation, 
        dispatch, 
        applyCouponHandler, 
        isCouponError, 
        isFocused, 
        setIsFocused, 
        promoCouponApplyHandler, 
        toCapitalized, 
        t
    }) => {
        return (
            <>
                <div id="discount-summary">
                    <div className="summary-order__discount-block">
                        <div 
                            className="summary-order__discount" 
                            style={{ textTransform: toCapitalized ? 'capitalize' : 'none' }}
                        >
                            <TextBox
                                id="promoCode"
                                className={(isFocused && couponText && isCouponError) ? 'danger-warn' : ''}                                
                                label={t('shopping_cart_details.promo_discount_code_placeholder')}
                                name="discount-code"
                                placeholder=" "
                                type="text"
                                value={couponText}
                                onChange={(event) => handleCouponChange(event, coupon, error, currentOperation, dispatch)}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => couponText === '' && setIsFocused(false)}
                            />
                            <Button
                                id="applyDiscount"
                                className={`btn btn--brand input-apply-btn ${(isFocused || couponText || isCouponError) ? '' : 'hidden'} ${(isFocused && couponText && isCouponError) ? 'danger-warn' : ''}`}
                                type="submit"
                                onClick={applyCouponHandler}
                            >
                                {t('shopping_cart_details.apply')}
                            </Button>
                        </div>
                        <div 
                            id="invalid-discount-summary" 
                            className={isCouponError ? 'invalid-coupon-error' : ''}
                        >
                            {(isFocused && couponText && isCouponError) && (validatedCoupon?.state === CouponState.EXPIRED ? 
                                t('shopping_bag.expired_discount_coupon') : 
                                (validatedCoupon?.state === CouponState.INVALID) ? 
                                        t('shopping_bag.invalid_discount_coupon') : 
                                        t('order_summary.oops_error'))}
                        </div>
                    </div>
                </div>
    
                <PromoCodes applyCouponHandlerChild={promoCouponApplyHandler} />
    
                <div className='one-promo-info'>
                    {t('order_summary.one_promo_at_a_time')}
                </div>
            </>
        );
    };
    

    
    return (
        <>
            <div id="summary-order" className="summary-order__shopping-bag" data-taxidentificationnumber="">
                <div className="summary-order__title" style={{ textTransform: toCapitalized ? 'capitalize' : 'none' }}>
                    <h2 className="mega">{t('shopping_cart_details.orderSummaryTitle')}</h2>
                </div>
                <div className={`summary-order__item ${config('isNASite') === true ? 'naChargesApplicable' : ''}`}>
                    <span>{currentPage !== Page.SHOPPINGBAG  ? t('order_summary.subtotal') : t('shopping_cart_details.subTotal') }</span>
                    <span className="cart-subtotal">{currencyFormatter(cart.cartSubtotal, cart.fractionDigits)}</span>
                </div>
                {config('isNASite') !== true &&
                    <>
                        <div className="summary-order__item">
                            <span>{t('shopping_cart_details.shippingInfoLabel')}</span><span>--</span>
                        </div>
                        <div><sub>{t('shopping_cart_details.subText')}</sub></div>
                        <div className="summary-order__item">
                            <span>{t('order_summary.vat')}</span>
                            <span id="total-VAT">{currencyFormatter(cart.VAT, cart.fractionDigits)}</span>
                        </div>
                        {cart.USBTax > 0 &&
                            <div className="summary-order__item">
                                <span>{t('shopping_cart_details.usbTax')}</span>
                                <span id="total-USBTAX">{currencyFormatter(cart.USBTax, cart.fractionDigits)}</span>
                            </div>
                        }
                        <div className="summary-order__item total-amount">
                            <span className="kilo">{t('shopping_cart_details.subTotalInclVat')}</span>
                            <span className="giga brand bold">
                                <span id="total-price">{currencyFormatter(cart.cartTotal, cart.fractionDigits)}</span>
                            </span>
                        </div>
                    </>
                }

                {cart?.validDiscountCode  && cart.totalSavingWithoutShippingDiscount > 0 && config('isNASite') !== true &&
                    <div id="totalCartSaving-section" className="summary-order__item  naChargesApplicable">
                        <span></span>
                        <span className="you-saved__amount naChargesApplicable">
                            <span>{t('shopping_cart_details.you_saved').replace('[X]', currencyFormatter(cart.totalSavingWithoutShippingDiscount, cart.fractionDigits))}!</span>
                        </span>
                    </div>
                }

                {getTaxShippingAndSaving(cart,currentPage,globalThresholdMet,isGlobalCartDiscountFree,globalDiscountShippingType,coupon,isThresholdMet,isShippingDisc,isShippingCouponFree)}
                {showPayPalPayLaterOffer && <div data-pp-message data-pp-placement="cart" data-pp-amount={totalCartAmount} data-pp-style-layout="text" data-pp-style-text-size="14" data-pp-style-text-align="center" data-pp-logo-type="inline" data-pp-onclick={`payPalPayLaterMessageClickHandler('${GaPageType.SHOPPINGBAG}')`} data-pp-buyercountry={config('regionCode')} style={{marginTop:'1.5rem'}}></div>}
               {globalDiscountData?.id && <GlobalDiscountSummary cart={cart} globalDiscountData={globalDiscountData} />}
                {(validatedCoupon?.state !== CouponState.INITIAL) &&
                    <CouponSummary removeCouponHandler={removeCouponHandler} cart={cart} doesDiscountExist={doesDiscountExist}/>
                }
                { !doesDiscountExist &&   
                    <div className='promo-parent'>
                        <CustomAccordion isExpanded={isExpanded} toggleAccordion={togglePromoAccordion} expandIcon={<DropdownArrow />} >
                            <span>{t('shopping_cart_details.promo_discount_code_placeholder')}</span>
                            {PromoCodeManager({
                                couponText, 
                                handleCouponChange, 
                                coupon, 
                                error, 
                                currentOperation, 
                                dispatch, 
                                applyCouponHandler, 
                                isCouponError, 
                                isFocused, 
                                setIsFocused, 
                                promoCouponApplyHandler, 
                                toCapitalized, 
                                t
                            })}
                        </CustomAccordion>
                    </div>
                }    
                


                {
                    error?.isError &&
                    (( error?.errorObj.operation === CartOperation.ADDCOUPON ||
                        error?.errorObj.operation === CartOperation.REMOVECOUPON
                    ) || ((currentOperation?.name === CartOperation.REMOVECOUPON) && error?.errorObj.operation === CartOperation.GETCART)) &&
                    <div className='error'>{error.errorObj.message}</div>
                }

                <div className="top-8">
                    {cart?.isBelowMOQInCart && !cart.isOutOfStockInCart &&
                        <div className='moq-warning-order-summary'>
                            <NoEntry />
                            <span>{t('moq.cart_level_warning')} </span>
                        </div>
                    }
                    {cart?.isOutOfStockInCart && 
                        <div className='oos-warning-order-summary'>
                            <NoEntry />
                            <span>{t('oos.remove_out_of_stock_items')}</span>
                        </div>
                    }
                    {config('isNASite') === true && showPaymentError && <div className="order-summary-error-msg-box">
                        <p className="order-summary-error-title">{t('checkout_payment.paymentErrorTitle')}</p>
                        <p>{t('checkout_payment.expressPayCheckoutError')}</p>
                    </div>}

                    <div className={`orderSummarySubmitBtnLayout ${(cart?.isOutOfStockInCart || cart?.isBelowMOQInCart) ? 'disable-navigation-overlay' : ''}`}>
                        <button id="proceedToCheckoutButton" disabled={cart?.isOutOfStockInCart || cart?.isBelowMOQInCart} className="shipping-bag-button" style={{ textTransform: config('withoutCapitalized')?.includes(config('locale')) ? 'none': toCapitalized  ? 'capitalize' : 'none' }} onClick={onCheckoutButtonClick}>
                            <span>{proceedToCheckout}</span>
                        </button>
                    </div>

                    {config('isNASite') === true && config('isPaypalEnabled') === 'true' &&
                        <div id="paypal-button" className={(cart?.isOutOfStockInCart || cart?.isBelowMOQInCart) ? 'disable-navigation-overlay' : ''}>
                            <div className='paypal-skeleton'></div>
                        </div>}
                    
                    {config('locale') === 'en-US' && isApplePaySupported() &&
                        <div className={`applePay-button ${(cart?.isOutOfStockInCart || cart?.isBelowMOQInCart) ? 'disable-navigation-overlay' : ''}`}>
                            <ApplePay 
                                isExpressCheckout={true}
                                cart={cart}
                                totalCartAmount={cartSubtotal}
                                dispatch={dispatch}
                                setShowPaymentError={setShowPaymentError}
                                startLoading={startLoading}
                                stopLoading={stopLoading}
                            />
                        </div>
                    }
                </div>
  
            <div className="text-center top-16">
                <a className="link--underline" href={config('regionCode') === 'GB' ? '/uk/' : `/${config('regionCode').toLowerCase()}/`} onClick={() => cartInteractionEvent(CartInteraction.CONTINUE_SHOPPING)}>{t('order_confirmation.continue')}</a>
            </div>
                

            </div>
            {config('regionCode') === 'US' &&
                <div className="higherTaxInUS">
                    {t('shopping_cart_details.higher_rates_US')}
                </div>
            }
        </>
    )
}

export default OrderSummary;
